/**
 * SRS / RoofHub payment-history CSV import (Sep 14, 2026) — client half.
 *
 * The browser only PARSES: read the CSV, auto-map the columns, normalize
 * money + dates, and hand typed lines to `material_payments_import`, which
 * does the invoice matching and classification server-side and returns a
 * plan. The same RPC applies the plan (p_dry_run=false) — one code path, so
 * what the review table shows is exactly what gets written. See
 * supabase/migrations/20260914_material_invoice_payments.sql.
 */

import { supabase } from "./supabase";
import { parseDateISO, parseMoney } from "./csvParse";

export type ImportField =
  | "invoice_number" | "amount" | "payment_date" | "payment_total"
  | "confirmation_number" | "payment_method" | "payment_account" | "status" | "description";

export const IMPORT_FIELDS: Array<{ key: ImportField; label: string; required: boolean; hint: string }> = [
  { key: "invoice_number", label: "Invoice #", required: true, hint: "RoofHub: Invoice Number" },
  { key: "amount", label: "Amount applied", required: true, hint: "RoofHub: Amount (negative = credit memo)" },
  { key: "payment_date", label: "Payment date", required: true, hint: "RoofHub: Payment Date" },
  { key: "payment_total", label: "Payment total", required: false, hint: "RoofHub: Amount Paid (the whole payment)" },
  { key: "confirmation_number", label: "Confirmation #", required: false, hint: "RoofHub: Confirmation Number" },
  { key: "payment_method", label: "Method", required: false, hint: "RoofHub: Payment Method" },
  { key: "payment_account", label: "Account", required: false, hint: "RoofHub: Payment Account" },
  { key: "status", label: "Status", required: false, hint: "RoofHub: Status — only Complete lines are applied" },
  { key: "description", label: "Description", required: false, hint: "RoofHub: Description" },
];

/** field → header column index */
export type ColumnMap = Partial<Record<ImportField, number>>;

const norm = (h: string) => h.toLowerCase().replace(/[^a-z0-9]/g, "");

// Exact (normalized) header names per field, most specific first. "Amount
// Paid" is the PAYMENT total in RoofHub, so it must never win "amount".
const EXACT: Record<ImportField, string[]> = {
  invoice_number: ["invoicenumber", "invoiceno", "invoicenum", "invoice", "invnumber", "invno", "inv", "documentnumber", "docnumber"],
  amount: ["amount", "amountapplied", "appliedamount", "applied", "invoiceamount", "lineamount", "amountdue"],
  payment_total: ["amountpaid", "paymentamount", "paymenttotal", "totalpaid", "checkamount", "total"],
  payment_date: ["paymentdate", "paiddate", "datepaid", "date", "transactiondate", "postdate", "postingdate"],
  confirmation_number: ["confirmationnumber", "confirmation", "confirmationno", "confirmationid", "referencenumber", "reference", "refnumber", "ref", "checknumber", "checkno", "check", "paymentnumber", "paymentid", "transactionid"],
  payment_method: ["paymentmethod", "method", "paymenttype"],
  payment_account: ["paymentaccount", "account", "bankaccount", "fromaccount"],
  status: ["status", "paymentstatus"],
  description: ["description", "memo", "notes", "note", "comment"],
};

/** Best-effort header → field mapping; anything unmatched stays unmapped for the user to pick. */
export function detectColumns(headers: string[]): ColumnMap {
  const map: ColumnMap = {};
  const used = new Set<number>();
  const normalized = headers.map(norm);
  for (const f of IMPORT_FIELDS) {
    for (const cand of EXACT[f.key]) {
      const idx = normalized.findIndex((h, i) => h === cand && !used.has(i));
      if (idx >= 0) { map[f.key] = idx; used.add(idx); break; }
    }
  }
  // loose fallbacks for the three required fields only
  const contains = (pred: (h: string) => boolean) => normalized.findIndex((h, i) => pred(h) && !used.has(i));
  if (map.invoice_number == null) {
    const i = contains((h) => h.includes("invoice") && !h.includes("amount") && !h.includes("date"));
    if (i >= 0) { map.invoice_number = i; used.add(i); }
  }
  if (map.amount == null) {
    const i = contains((h) => h.includes("amount") && !h.includes("paid") && !h.includes("total"));
    if (i >= 0) { map.amount = i; used.add(i); }
  }
  if (map.payment_date == null) {
    const i = contains((h) => h.includes("date") && !h.includes("create") && !h.includes("due") && !h.includes("invoice"));
    if (i >= 0) { map.payment_date = i; used.add(i); }
  }
  return map;
}

export interface RawLine {
  /** 0-based position in the data rows = the `idx` the server echoes back */
  idx: number;
  raw: Record<string, string>;
  invoice_number: string;
  amount: number | null;
  payment_date: string | null;
  payment_total: number | null;
  confirmation_number: string;
  payment_method: string | null;
  payment_account: string | null;
  status: string | null;
  description: string | null;
  /** client-side validation notes (server also refuses these as skip_invalid) */
  problems: string[];
}

export function buildLines(rows: string[][], headers: string[], map: ColumnMap): RawLine[] {
  const cell = (r: string[], f: ImportField): string => {
    const i = map[f];
    return i == null ? "" : (r[i] ?? "").trim();
  };
  return rows.map((r, idx) => {
    const raw: Record<string, string> = {};
    headers.forEach((h, i) => { raw[h] = r[i] ?? ""; });
    const invoice_number = cell(r, "invoice_number");
    const amountS = cell(r, "amount");
    const dateS = cell(r, "payment_date");
    const amount = parseMoney(amountS);
    const payment_date = parseDateISO(dateS);
    const problems: string[] = [];
    if (!invoice_number) problems.push("no invoice #");
    if (amount == null) problems.push(amountS ? `can't read amount "${amountS}"` : "no amount");
    if (!payment_date) problems.push(dateS ? `can't read date "${dateS}"` : "no payment date");
    return {
      idx, raw, invoice_number, amount, payment_date,
      payment_total: parseMoney(cell(r, "payment_total")),
      confirmation_number: cell(r, "confirmation_number"),
      payment_method: cell(r, "payment_method") || null,
      payment_account: cell(r, "payment_account") || null,
      status: cell(r, "status") || null,
      description: cell(r, "description") || null,
      problems,
    };
  });
}

/** The exact p_lines element shape the RPC reads. */
export function toRpcLines(lines: RawLine[]): Array<Record<string, unknown>> {
  return lines.map((l) => ({
    invoice_number: l.invoice_number,
    amount: l.amount,
    payment_date: l.payment_date,
    payment_total: l.payment_total,
    confirmation_number: l.confirmation_number,
    payment_method: l.payment_method,
    payment_account: l.payment_account,
    status: l.status,
    description: l.description,
    raw: l.raw,
  }));
}

export type LineOutcome =
  | "mark_paid" | "already_paid" | "partial" | "create_stub"
  | "skip_duplicate" | "skip_status" | "skip_unmatched" | "skip_ambiguous" | "skip_invalid";

export interface PlanLine {
  idx: number;
  invoice_number: string | null;
  invoice_number_canonical: string | null;
  amount: number | null;
  payment_date: string | null;
  payment_total: number | null;
  confirmation_number: string;
  status: string | null;
  match: "exact" | "normalized" | "base" | "ambiguous" | "none";
  invoice_id: string | null;
  invoice_total: number | null;
  job_name: string | null;
  job_address: string | null;
  deal_id: string | null;
  homeowner_name: string | null;
  paid_before: "unpaid" | "paid" | null;
  paid_at_before: string | null;
  applied_before: number | null;
  applied_after: number | null;
  covered_after: boolean | null;
  fills_paid_at: boolean;
  stub_total: number | null;
  outcome: LineOutcome;
}

export interface PlanPayment {
  confirmation_number: string;
  payment_date: string;
  payment_total: number | null;
  line_count: number;
  line_sum: number;
  reconciles: boolean;
}

export interface PlanSummary {
  lines: number;
  mark_paid: number;
  mark_paid_invoices: number;
  mark_paid_total: number;
  already_paid: number;
  fills_paid_at: number;
  partial: number;
  create_stub: number;
  skip_duplicate: number;
  skip_status: number;
  skip_unmatched: number;
  skip_ambiguous: number;
  skip_invalid: number;
  applied_total: number;
  inserted_payments: number;
  created_invoices: number;
}

export interface ImportPlan {
  batch_id: string;
  dry_run: boolean;
  lines: PlanLine[];
  payments: PlanPayment[];
  summary: PlanSummary;
}

export interface ImportOptions {
  supplier?: string;
  create_missing: boolean;
  batch_id?: string;
  file_name?: string;
  storage_path?: string | null;
}

export const OUTCOME_META: Record<LineOutcome, { label: string; tone: "emerald" | "slate" | "amber" | "sky" | "rose"; selectable: boolean }> = {
  mark_paid:      { label: "Will mark paid",            tone: "emerald", selectable: true },
  already_paid:   { label: "Already paid",              tone: "slate",   selectable: true },
  partial:        { label: "Partial — stays unpaid",    tone: "amber",   selectable: true },
  create_stub:    { label: "Not in CRM — create",       tone: "sky",     selectable: true },
  skip_duplicate: { label: "Already imported",          tone: "slate",   selectable: false },
  skip_status:    { label: "Skipped — not complete",    tone: "rose",    selectable: false },
  skip_unmatched: { label: "Not in CRM — skipped",      tone: "amber",   selectable: false },
  skip_ambiguous: { label: "Ambiguous invoice #",       tone: "rose",    selectable: false },
  skip_invalid:   { label: "Unreadable line",           tone: "rose",    selectable: false },
};

/** Dry run (plan) or apply. supabase-js RESOLVES with {error} — never throws — so surface it here. */
export async function runPaymentImport(
  lines: Array<Record<string, unknown>>, options: ImportOptions, dryRun: boolean,
): Promise<ImportPlan> {
  const { data, error } = await supabase.rpc("material_payments_import", {
    p_lines: lines, p_options: options, p_dry_run: dryRun,
  });
  if (error) throw new Error(error.message);
  const plan = data as ImportPlan;
  if (!plan || !Array.isArray(plan.lines)) throw new Error("Import returned no plan.");
  return plan;
}

export async function undoPaymentImport(batchId: string): Promise<{ deleted_payments: number; deleted_invoices: number }> {
  const { data, error } = await supabase.rpc("material_payments_import_undo", { p_batch_id: batchId });
  if (error) throw new Error(error.message);
  return data as { deleted_payments: number; deleted_invoices: number };
}
